import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Modal, DatePicker, Tooltip, Badge, Tabs } from 'antd';
import { 
  SearchOutlined, 
  DownloadOutlined, 
  ReloadOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  BoxPlotOutlined,
  UserOutlined,
  ShopOutlined,
  GlobalOutlined,
  CaretUpOutlined, 
  CaretDownOutlined,
  DownOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import NavbarDropshipping from '../navbarDropshipping/navbarDropshipping';
import './misPedidos.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const ProductTablePedidos = () => {
  const [data, setData] = useState([]);
  const [externalData, setExternalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredExternalData, setFilteredExternalData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [externalSearchText, setExternalSearchText] = useState('');
  const [filters, setFilters] = useState({
    orderStatus: '',
    carrier: '',
    warehouse: '',
  });
  const [externalFilters, setExternalFilters] = useState({
    orderStatus: '',
    carrier: '',
  });
  
  // Estados para controlar dropdowns personalizados
  const [showCarrierDropdown, setShowCarrierDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showExternalCarrierDropdown, setShowExternalCarrierDropdown] = useState(false);
  const [showExternalStatusDropdown, setShowExternalStatusDropdown] = useState(false);
  
  // Referencias para detectar clics fuera de los dropdowns
  const carrierRef = useRef(null);
  const statusRef = useRef(null);
  const externalCarrierRef = useRef(null);
  const externalStatusRef = useRef(null);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isTableScrolled, setIsTableScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [externalLoading, setExternalLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    entregados: 0,
    enDistribucion: 0,
    rechazados: 0
  });
  const [externalStats, setExternalStats] = useState({
    total: 0,
    entregados: 0,
    enDistribucion: 0,
    rechazados: 0
  });
  const [activeTab, setActiveTab] = useState('internal');
  
  // Estados para la tabla personalizada
  const [currentPage, setCurrentPage] = useState(1);
  const [externalCurrentPage, setExternalCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [externalItemsPerPage, setExternalItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'orderDate', direction: 'descending' });
  const [externalSortConfig, setExternalSortConfig] = useState({ key: 'orderDate', direction: 'descending' });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedExternalItems, setSelectedExternalItems] = useState([]);

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);

  // Mapeo de los estados de la orden
  const orderStatusMap = {
    1: 'Creado',
    2: 'Distribucion',
    3: 'Entregado',
    4: 'Rechazado',
    5: 'Devolucion',
    6: 'Anulado',
    7: 'Otro',
    8: 'Error'
  };

  // Función para consumir la API de productos internos
  const user_id = localStorage.getItem('usuario_id');
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.99envios.app/api/listar_ordenes/${user_id}`);
      const result = await response.json();
      const formattedData = result
        .filter(item => item.origen_creacion === 2 || item.origen_creacion === 4) // Modified filter condition
        .map((item, index) => ({
          key: item.id?.toString() || index.toString(),
          id: item.id || index + 1,
          productName: item.nombre_producto,
          preenvioNumber: item.preeenvio.numero_preenvio,
          id_preenvio: item.preeenvio.id,
          orderDate: item.fecha_estado,
          orderDateObj: new Date(item.fecha_estado),
          client: `${item.preeenvio.destinatario.nombre} ${item.preeenvio.destinatario.primer_apellido} ${item.preeenvio.destinatario.segundo_apellido || ''}`.trim(),
          phone: item.preeenvio.destinatario.telefono,
          orderStatus: item.preeenvio.estado?.nombre || 'Desconocido',
          carrier: item.transportadora,
          shippingType: item.estado_envio,
          rawData: item,
          selected: false,
        }))
        .sort((a, b) => {
          const dateA = new Date(a.orderDate);
          const dateB = new Date(b.orderDate);
          return dateB - dateA;
        });
      setData(formattedData);
      setFilteredData(formattedData);
      calculateStats(formattedData);
    } catch (error) {
      console.error('Error fetching internal orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para consumir la API de productos externos (dropshipping)
  const fetchExternalOrders = async () => {
    setExternalLoading(true);
    try {
      const response = await fetch(`https://api.99envios.app/api/listar_ordenes_dropshipping/${user_id}`);
      const result = await response.json();
      const formattedData = result.map((item, index) => ({
        key: item.preeenvio?.id?.toString() || index.toString(),
        id: item.preeenvio?.id || index + 1,
        productName: item.nombre_producto,
        preenvioNumber: item.preeenvio?.numero_preenvio || 'N/A',
        id_preenvio: item.preeenvio?.id,
        orderDate: item.fecha_estado,
        orderDateObj: new Date(item.fecha_estado),
        client: `${item.preeenvio?.destinatario?.nombre || ''} ${item.preeenvio?.destinatario?.primer_apellido || ''} ${item.preeenvio?.destinatario?.segundo_apellido || ''}`.trim(),
        phone: item.preeenvio?.destinatario?.telefono || 'N/A',
        orderStatus: item.preeenvio?.estado?.nombre || item.estado_envio || 'Desconocido',
        carrier: item.transportadora,
        shippingType: item.estado_envio,
        valorVenta: item.valor_venta,
        valorProveedor: item.valor_proveedor,
        valorUtilidad: item.valor_utilidad,
        rawData: item,
        selected: false,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.orderDate);
        const dateB = new Date(b.orderDate);
        return dateB - dateA;
      });
      setExternalData(formattedData);
      setFilteredExternalData(formattedData);
      calculateExternalStats(formattedData);
    } catch (error) {
      console.error('Error fetching external orders:', error);
    } finally {
      setExternalLoading(false);
    }
  };

  // Función para calcular estadísticas de productos internos
  const calculateStats = (orders) => {
    const stats = {
      total: orders.length,
      entregados: orders.filter(order => order.orderStatus.toLowerCase() === 'entregado').length,
      enDistribucion: orders.filter(order => order.orderStatus.toLowerCase() === 'creado').length,
      rechazados: orders.filter(order => 
        ['error', 'anulado', 'desactivado'].includes(order.orderStatus.toLowerCase())
      ).length
    };
    setStats(stats);
  };

  // Función para calcular estadísticas de productos externos
  const calculateExternalStats = (orders) => {
    const stats = {
      total: orders.length,
      entregados: orders.filter(order => order.orderStatus.toLowerCase() === 'entregado').length,
      enDistribucion: orders.filter(order => order.orderStatus.toLowerCase() === 'creado').length,
      rechazados: orders.filter(order => 
        ['error', 'anulado', 'desactivado'].includes(order.orderStatus.toLowerCase())
      ).length
    };
    setExternalStats(stats);
  };

  // Carga los datos al montar el componente
  useEffect(() => {
    fetchOrders();
    fetchExternalOrders();
  }, []);

  // Sincronizar el desplazamiento de las barras
  const syncScroll = (sourceRef, targetRef) => {
    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (carrierRef.current && !carrierRef.current.contains(event.target)) {
        setShowCarrierDropdown(false);
      }
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (externalCarrierRef.current && !externalCarrierRef.current.contains(event.target)) {
        setShowExternalCarrierDropdown(false);
      }
      if (externalStatusRef.current && !externalStatusRef.current.contains(event.target)) {
        setShowExternalStatusDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleExternalSearch = (value) => {
    setExternalSearchText(value);
    setExternalCurrentPage(1); // Reset to first page on search
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleExternalFilterChange = (filterName, value) => {
    setExternalFilters({ ...externalFilters, [filterName]: value });
    setExternalCurrentPage(1); // Reset to first page on filter change
  };

  useEffect(() => {
    let result = data;
    if (searchText) {
      result = result.filter(
        (item) => 
          item.id.toString().includes(searchText) ||
          item.productName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.preenvioNumber.toLowerCase().includes(searchText.toLowerCase()) ||
          item.client.toLowerCase().includes(searchText.toLowerCase()) ||
          item.phone.includes(searchText)
      );
    }
    if (filters.orderStatus) {
      result = result.filter((item) => item.orderStatus.toLowerCase() === filters.orderStatus.toLowerCase());
    }
    if (filters.carrier) {
      result = result.filter((item) => item.carrier === filters.carrier);
    }
    if (filters.warehouse) {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }
    
    // Mantener el ordenamiento por fecha después de filtrar
    result = result.sort((a, b) => {
      const dateA = new Date(a.orderDate);
      const dateB = new Date(b.orderDate);
      return dateB - dateA; // Descendente: más reciente primero
    });
    
    setFilteredData(result);
  }, [searchText, filters, data]);

  // Filtrado para productos externos
  useEffect(() => {
    let result = externalData;
    if (externalSearchText) {
      result = result.filter(
        (item) => 
          item.id.toString().includes(externalSearchText) ||
          item.productName.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.preenvioNumber.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.client.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.phone.includes(externalSearchText)
      );
    }
    if (externalFilters.orderStatus) {
      result = result.filter((item) => item.orderStatus.toLowerCase() === externalFilters.orderStatus.toLowerCase());
    }
    if (externalFilters.carrier) {
      result = result.filter((item) => item.carrier === externalFilters.carrier);
    }
    
    // Mantener el ordenamiento por fecha después de filtrar
    result = result.sort((a, b) => {
      const dateA = new Date(a.orderDate);
      const dateB = new Date(b.orderDate);
      return dateB - dateA;
    });
    
    setFilteredExternalData(result);
  }, [externalSearchText, externalFilters, externalData]);

  const showModal = (record) => {
    setSelectedOrder(record.rawData);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'creado': 'status-creado',
      'entregado': 'status-entregado',
      'error': 'status-error',
      'anulado': 'status-anulado',
      'desactivado': 'status-desactivado'
    };
    return `status-badge ${statusMap[status?.toLowerCase()] || 'status-otro'}`;
  };

  // Funciones para la tabla personalizada
  const handleCheckboxChange = (key) => {
    const updatedData = data.map(item => 
      item.key === key ? { ...item, selected: !item.selected } : item
    );
    setData(updatedData);
    setFilteredData(updatedData);
    setSelectedItems(updatedData.filter(item => item.selected).map(item => item.key));
  };

  const handleExternalCheckboxChange = (key) => {
    const updatedData = externalData.map(item => 
      item.key === key ? { ...item, selected: !item.selected } : item
    );
    setExternalData(updatedData);
    setFilteredExternalData(updatedData);
    setSelectedExternalItems(updatedData.filter(item => item.selected).map(item => item.key));
  };

  // Función para ordenar datos
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Función para ordenar datos externos
  const requestExternalSort = (key) => {
    let direction = 'ascending';
    if (externalSortConfig.key === key && externalSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setExternalSortConfig({ key, direction });
  };

  // Datos ordenados y paginados para productos internos
  const sortedData = useMemo(() => {
    let sortableItems = [...filteredData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        // Manejar ordenamiento para fechas
        if (sortConfig.key === 'orderDate') {
          const dateA = new Date(a.orderDate);
          const dateB = new Date(b.orderDate);
          if (sortConfig.direction === 'ascending') {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }

        // Ordenamiento para strings
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);

  // Datos ordenados y paginados para productos externos
  const sortedExternalData = useMemo(() => {
    let sortableItems = [...filteredExternalData];
    if (externalSortConfig.key) {
      sortableItems.sort((a, b) => {
        // Manejar ordenamiento para fechas
        if (externalSortConfig.key === 'orderDate') {
          const dateA = new Date(a.orderDate);
          const dateB = new Date(b.orderDate);
          if (externalSortConfig.direction === 'ascending') {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }

        // Manejar ordenamiento para números
        if (['valorVenta', 'valorProveedor', 'valorUtilidad'].includes(externalSortConfig.key)) {
          if (externalSortConfig.direction === 'ascending') {
            return a[externalSortConfig.key] - b[externalSortConfig.key];
          } else {
            return b[externalSortConfig.key] - a[externalSortConfig.key];
          }
        }

        // Ordenamiento para strings
        if (a[externalSortConfig.key] < b[externalSortConfig.key]) {
          return externalSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[externalSortConfig.key] > b[externalSortConfig.key]) {
          return externalSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredExternalData, externalSortConfig]);

  // Paginación para productos internos
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Paginación para productos externos
  const externalIndexOfLastItem = externalCurrentPage * externalItemsPerPage;
  const externalIndexOfFirstItem = externalIndexOfLastItem - externalItemsPerPage;
  const currentExternalItems = sortedExternalData.slice(externalIndexOfFirstItem, externalIndexOfLastItem);
  const externalTotalPages = Math.ceil(sortedExternalData.length / externalItemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const paginateExternal = (pageNumber) => setExternalCurrentPage(pageNumber);

  // Función para renderizar el encabezado de la columna con indicador de orden
  const renderColumnHeader = (key, title) => {
    const isActive = sortConfig.key === key;
    return (
      <div 
        className="table-header-cell"
        onClick={() => requestSort(key)}
      >
        {title}
        {isActive && (
          <span className="sort-indicator">
            {sortConfig.direction === 'ascending' ? <CaretUpOutlined /> : <CaretDownOutlined />}
          </span>
        )}
      </div>
    );
  };

  // Función para renderizar el encabezado de la columna con indicador de orden para productos externos
  const renderExternalColumnHeader = (key, title) => {
    const isActive = externalSortConfig.key === key;
    return (
      <div 
        className="table-header-cell"
        onClick={() => requestExternalSort(key)}
      >
        {title}
        {isActive && (
          <span className="sort-indicator">
            {externalSortConfig.direction === 'ascending' ? <CaretUpOutlined /> : <CaretDownOutlined />}
          </span>
        )}
      </div>
    );
  };

  const exportToExcel = () => {
    // Determine which data to export based on active tab
    const selectedData = activeTab === 'internal' 
      ? data.filter(item => selectedItems.includes(item.key))
      : externalData.filter(item => selectedExternalItems.includes(item.key));
    
    if (selectedData.length === 0) {
      Modal.warning({
        title: 'Selección requerida',
        content: 'Por favor seleccione al menos un pedido para exportar.'
      });
      return;
    }
    
    // Prepare data for Excel by formatting and selecting relevant columns
    const exportData = activeTab === 'internal' 
      ? selectedData.map(item => ({
          'ID': item.id,
          'Nombre del producto': item.productName,
          'Número de preenvío': item.preenvioNumber,
          'Fecha de la orden': new Date(item.orderDate).toLocaleDateString('es-CO'),
          'Cliente': item.client,
          'Teléfono': item.phone,
          'Estatus de la Orden': item.orderStatus,
          'Transportadora': item.carrier,
          'Tipo de Envío': item.shippingType
        }))
      : selectedData.map(item => ({
          'ID': item.id,
          'Nombre del producto': item.productName,
          'Número de preenvío': item.preenvioNumber,
          'Valor Venta': item.valorVenta,
          'Valor Proveedor': item.valorProveedor,
          'Utilidad': item.valorUtilidad,
          'Fecha de la orden': new Date(item.orderDate).toLocaleDateString('es-CO'),
          'Cliente': item.client,
          'Teléfono': item.phone,
          'Estatus de la Orden': item.orderStatus,
          'Transportadora': item.carrier
        }));
    
    // Create Excel worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Create Excel workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');
    
    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, `pedidos_${activeTab}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleTableScroll = (e) => {
    setIsTableScrolled(e.target.scrollTop > 0);
    syncScroll(tableContainerRef, topScrollRef);
  };

  // Función para actualizar datos
  const handleRefresh = () => {
    if (activeTab === 'internal') {
      fetchOrders();
    } else {
      fetchExternalOrders();
    }
  };

  // Renderizado de la paginación
  const renderPagination = (currentPage, totalPages, paginate, itemsPerPage, setItemsPerPage, totalItems, firstIndex, lastIndex) => {
    return (
      <div className="custom-pagination">
        <div className="pagination-info">
          Mostrando {firstIndex + 1} a {Math.min(lastIndex, totalItems)} de {totalItems} registros
        </div>
        <div className="pagination-controls">
          {/* Mostrar botón "1" solo si no está ya visible en los botones centrales */}
          {currentPage > 2 && (
            <button 
              onClick={() => paginate(1)} 
              className="pagination-button"
            >
              1
            </button>
          )}
          
          {/* Mostrar botón anterior solo si no estamos en la primera página */}
          {currentPage > 1 && (
            <button 
              onClick={() => paginate(currentPage - 1)} 
              className="pagination-button"
            >
              &lt;
            </button>
          )}
          
          {/* Números de página */}
          <div className="pagination-pages">
            {(() => {
              // Show only current page and one page before/after
              const pagesToShow = [];
              
              // Add previous page if it exists
              if (currentPage > 1) {
                pagesToShow.push(currentPage - 1);
              }
              
              // Add current page
              pagesToShow.push(currentPage);
              
              // Add next page if it exists
              if (currentPage < totalPages) {
                pagesToShow.push(currentPage + 1);
              }
              
              return pagesToShow.map(pageNum => (
                <button 
                  key={pageNum}
                  onClick={() => paginate(pageNum)}
                  className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              ));
            })()}
          </div>
          
          {/* Mostrar botón siguiente solo si no estamos en la última página */}
          {currentPage < totalPages && (
            <button 
              onClick={() => paginate(currentPage + 1)} 
              className="pagination-button"
            >
              &gt;
            </button>
          )}
          
          {/* Mostrar botón de última página solo si no está ya visible en los botones centrales */}
          {currentPage < totalPages - 1 && (
            <button 
              onClick={() => paginate(totalPages)} 
              className="pagination-button"
            >
              {totalPages}
            </button>
          )}
        </div>
        <div className="items-per-page">
          <span>Registros por página: </span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              paginate(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <>
      <NavbarDropshipping title="Envíos Dropshipping"/>
      <div className='misPedidos'>
        {/* Header con acciones */}
        <div className="page-header-mis-pedidos">
          <h1 className="page-title">Mis Pedidos</h1>
          <div className="page-actions">
            <button className="refresh-button" onClick={handleRefresh}>
              <ReloadOutlined />
            </button>
            <Button 
              className="excel-export-button" 
              onClick={exportToExcel}
              icon={<FileExcelOutlined />}
            >
              Exportar Excel
            </Button>
          </div>
        </div>

        {/* Pestañas para productos internos y externos */}
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          className="products-tabs"
          size="large"
        >
          <TabPane 
            tab={
              <span>
                <ShopOutlined />
                Productos Internos ({stats.total})
              </span>
            } 
            key="internal"
          >
            {/* Dashboard de estadísticas para productos internos */}
            <div className="stats-dashboard-mis-pedidos">
              <div className="stat-card stat-info">
                <div className="stat-icon">
                  <ShoppingCartOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.total}</h3>
                  <p>Total de Pedidos</p>
                </div>
              </div>
              <div className="stat-card stat-success">
                <div className="stat-icon">
                  <CheckCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.entregados}</h3>
                  <p>Entregados</p>
                </div>
              </div>
              <div className="stat-card stat-warning">
                <div className="stat-icon">
                  <CarOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.enDistribucion}</h3>
                  <p>En Distribución</p>
                </div>
              </div>
              <div className="stat-card stat-danger">
                <div className="stat-icon">
                  <CloseCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.rechazados}</h3>
                  <p>Rechazados</p>
                </div>
              </div>
            </div>

            {/* Filtros personalizados para productos internos */}
            <div className="misPedidos-misPedidos-header">
              <div className="custom-search-input">
                <input 
                  type="text" 
                  placeholder="Buscar por producto, guía, destinatario, teléfono..." 
                  onChange={(e) => handleSearch(e.target.value)}
                  className="search-input"
                />
                <div className="search-icon">
                  <SearchOutlined />
                </div>
              </div>
              
              {/* Custom Transportadora Dropdown */}
              <div className="custom-dropdown" ref={carrierRef}>
                <div 
                  className="custom-dropdown-selector"
                  onClick={() => setShowCarrierDropdown(!showCarrierDropdown)}
                >
                  <span>{filters.carrier || "Filtrar por Transportadora"}</span>
                  <span className="dropdown-arrow"><DownOutlined /></span>
                </div>
                {showCarrierDropdown && (
                  <div className="custom-dropdown-menu">
                    <div 
                      className={`custom-dropdown-item ${filters.carrier === '' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('carrier', '');
                        setShowCarrierDropdown(false);
                      }}
                    >
                      Todas
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.carrier === 'interrapidisimo' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('carrier', 'interrapidisimo');
                        setShowCarrierDropdown(false);
                      }}
                    >
                      Interrapidisimo
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.carrier === 'coordinadora' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('carrier', 'coordinadora');
                        setShowCarrierDropdown(false);
                      }}
                    >
                      Coordinadora
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.carrier === 'servientrega' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('carrier', 'servientrega');
                        setShowCarrierDropdown(false);
                      }}
                    >
                      Servientrega
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.carrier === 'envia' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('carrier', 'envia');
                        setShowCarrierDropdown(false);
                      }}
                    >
                      Envia
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Estado de Orden Dropdown */}
              <div className="custom-dropdown" ref={statusRef}>
                <div 
                  className="custom-dropdown-selector"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                >
                  <span>{filters.orderStatus || "Filtrar por Estatus de la Orden"}</span>
                  <span className="dropdown-arrow"><DownOutlined /></span>
                </div>
                {showStatusDropdown && (
                  <div className="custom-dropdown-menu">
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === '' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', '');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Todos
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === 'creado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', 'creado');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Creado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === 'entregado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', 'entregado');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Entregado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === 'anulado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', 'anulado');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Anulado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === 'error' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', 'error');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Error
                    </div>
                    <div 
                      className={`custom-dropdown-item ${filters.orderStatus === 'desactivado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleFilterChange('orderStatus', 'desactivado');
                        setShowStatusDropdown(false);
                      }}
                    >
                      Desactivado
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabla personalizada para productos internos */}
            <div className="misPedidos-tabla-wrapper">
              <div className="misPedidos-tabla misPedidos-custom-table-container">
                {loading && (
                  <div className="loading-overlay">
                    <div className="loading-spinner">
                      <ReloadOutlined spin />
                    </div>
                  </div>
                )}
                {filteredData.length > 0 ? (
                  <table className="misPedidos-custom-table">
                    <thead>
                      <tr>
                        <th className="checkbox-column"></th>
                        <th>{renderColumnHeader('id', '#')}</th>
                        <th>{renderColumnHeader('productName', 'Nombre del producto')}</th>
                        <th>{renderColumnHeader('preenvioNumber', 'Número de preenvío')}</th>
                        <th>{renderColumnHeader('orderDate', 'Fecha de la orden')}</th>
                        <th>{renderColumnHeader('client', 'Cliente')}</th>
                        <th>{renderColumnHeader('phone', 'Teléfono')}</th>
                        <th>{renderColumnHeader('orderStatus', 'Estatus de la Orden')}</th>
                        <th>{renderColumnHeader('carrier', 'Transportadora')}</th>
                        <th>{renderColumnHeader('shippingType', 'Tipo de Envío')}</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map(item => (
                        <tr key={item.key} className={item.selected ? 'selected-row' : ''}>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={() => handleCheckboxChange(item.key)}
                            />
                          </td>
                          <td>{item.id}</td>
                          <td>{item.productName}</td>
                          <td>{item.preenvioNumber}</td>
                          <td>
                            {new Date(item.orderDate).toLocaleDateString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </td>
                          <td>{item.client}</td>
                          <td>{item.phone}</td>
                          <td>
                            <span className={getStatusBadgeClass(item.orderStatus)}>
                              {item.orderStatus}
                            </span>
                          </td>
                          <td>{item.carrier}</td>
                          <td>{item.shippingType}</td>
                          <td>
                            <div className="row-actions">
                              <button className="ver-detalles-btn" onClick={() => showModal(item)}>
                                Detalles
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data-message">
                    <p>No hay datos disponibles</p>
                  </div>
                )}
              </div>
              
              {/* Paginación para productos internos */}
              {filteredData.length > 0 && renderPagination(
                currentPage,
                totalPages,
                paginate,
                itemsPerPage,
                setItemsPerPage,
                sortedData.length,
                indexOfFirstItem,
                indexOfLastItem
              )}
            </div>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <GlobalOutlined />
                Productos Externos ({externalStats.total})
              </span>
            } 
            key="external"
          >
            {/* Dashboard de estadísticas para productos externos */}
            <div className="stats-dashboard-mis-pedidos">
              <div className="stat-card stat-info">
                <div className="stat-icon">
                  <ShoppingCartOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.total}</h3>
                  <p>Total de Pedidos</p>
                </div>
              </div>
              <div className="stat-card stat-success">
                <div className="stat-icon">
                  <CheckCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.entregados}</h3>
                  <p>Entregados</p>
                </div>
              </div>
              <div className="stat-card stat-warning">
                <div className="stat-icon">
                  <CarOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.enDistribucion}</h3>
                  <p>En Distribución</p>
                </div>
              </div>
              <div className="stat-card stat-danger">
                <div className="stat-icon">
                  <CloseCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.rechazados}</h3>
                  <p>Rechazados</p>
                </div>
              </div>
            </div>

            {/* Filtros personalizados para productos externos */}
            <div className="misPedidos-misPedidos-header">
              <div className="custom-search-input">
                <input 
                  type="text" 
                  placeholder="Buscar por producto, guía, destinatario, teléfono..." 
                  onChange={(e) => handleExternalSearch(e.target.value)}
                  className="search-input"
                />
                <div className="search-icon">
                  <SearchOutlined />
                </div>
              </div>
              
              {/* Custom Transportadora Dropdown */}
              <div className="custom-dropdown" ref={externalCarrierRef}>
                <div 
                  className="custom-dropdown-selector"
                  onClick={() => setShowExternalCarrierDropdown(!showExternalCarrierDropdown)}
                >
                  <span>{externalFilters.carrier || "Filtrar por Transportadora"}</span>
                  <span className="dropdown-arrow"><DownOutlined /></span>
                </div>
                {showExternalCarrierDropdown && (
                  <div className="custom-dropdown-menu">
                    <div 
                      className={`custom-dropdown-item ${externalFilters.carrier === '' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('carrier', '');
                        setShowExternalCarrierDropdown(false);
                      }}
                    >
                      Todas
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.carrier === 'interrapidisimo' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('carrier', 'interrapidisimo');
                        setShowExternalCarrierDropdown(false);
                      }}
                    >
                      Interrapidisimo
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.carrier === 'coordinadora' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('carrier', 'coordinadora');
                        setShowExternalCarrierDropdown(false);
                      }}
                    >
                      Coordinadora
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.carrier === 'servientrega' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('carrier', 'servientrega');
                        setShowExternalCarrierDropdown(false);
                      }}
                    >
                      Servientrega
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.carrier === 'envia' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('carrier', 'envia');
                        setShowExternalCarrierDropdown(false);
                      }}
                    >
                      Envia
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Estado de Orden Dropdown para productos internos */}
              <div className="custom-dropdown" ref={externalStatusRef}>
                <div 
                  className="custom-dropdown-selector"
                  onClick={() => setShowExternalStatusDropdown(!showExternalStatusDropdown)}
                >
                  <span>{externalFilters.orderStatus || "Filtrar por Estatus de la Orden"}</span>
                  <span className="dropdown-arrow"><DownOutlined /></span>
                </div>
                {showExternalStatusDropdown && (
                  <div className="custom-dropdown-menu">
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === '' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', '');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Todos
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === 'creado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', 'creado');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Creado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === 'entregado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', 'entregado');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Entregado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === 'anulado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', 'anulado');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Anulado
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === 'error' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', 'error');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Error
                    </div>
                    <div 
                      className={`custom-dropdown-item ${externalFilters.orderStatus === 'desactivado' ? 'selected' : ''}`}
                      onClick={() => {
                        handleExternalFilterChange('orderStatus', 'desactivado');
                        setShowExternalStatusDropdown(false);
                      }}
                    >
                      Desactivado
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabla personalizada para productos externos */}
            <div className="misPedidos-tabla-wrapper">
              <div className="misPedidos-tabla misPedidos-custom-table-container">
                {externalLoading && (
                  <div className="loading-overlay">
                    <div className="loading-spinner">
                      <ReloadOutlined spin />
                    </div>
                  </div>
                )}
                {filteredExternalData.length > 0 ? (
                  <table className="misPedidos-custom-table">
                    <thead>
                      <tr>
                        <th className="checkbox-column"></th>
                        <th>{renderExternalColumnHeader('id', '#')}</th>
                        <th>{renderExternalColumnHeader('productName', 'Nombre del producto')}</th>
                        <th>{renderExternalColumnHeader('preenvioNumber', 'Número de preenvío')}</th>
                        <th>{renderExternalColumnHeader('valorVenta', 'Valor Venta')}</th>
                        <th>{renderExternalColumnHeader('valorProveedor', 'Valor Proveedor')}</th>
                        <th>{renderExternalColumnHeader('valorUtilidad', 'Utilidad')}</th>
                        <th>{renderExternalColumnHeader('orderDate', 'Fecha de la orden')}</th>
                        <th>{renderExternalColumnHeader('client', 'Cliente')}</th>
                        <th>{renderExternalColumnHeader('phone', 'Teléfono')}</th>
                        <th>{renderExternalColumnHeader('orderStatus', 'Estatus de la Orden')}</th>
                        <th>{renderExternalColumnHeader('carrier', 'Transportadora')}</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentExternalItems.map(item => (
                        <tr key={item.key} className={item.selected ? 'selected-row' : ''}>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={() => handleExternalCheckboxChange(item.key)}
                            />
                          </td>
                          <td>{item.id}</td>
                          <td>{item.productName}</td>
                          <td>{item.preenvioNumber}</td>
                          <td>
                            <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              ${item.valorVenta?.toLocaleString('es-CO') || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#f57c00' }}>
                              ${item.valorProveedor?.toLocaleString('es-CO') || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                              ${item.valorUtilidad?.toLocaleString('es-CO') || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {new Date(item.orderDate).toLocaleDateString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </td>
                          <td>{item.client}</td>
                          <td>{item.phone}</td>
                          <td>
                            <span className={getStatusBadgeClass(item.orderStatus)}>
                              {item.orderStatus}
                            </span>
                          </td>
                          <td>{item.carrier}</td>
                          <td>
                            <div className="row-actions">
                              <button className="ver-detalles-btn" onClick={() => showModal(item)}>
                                Detalles
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data-message">
                    <p>No hay datos disponibles</p>
                  </div>
                )}
              </div>
              
              {/* Paginación para productos externos */}
              {filteredExternalData.length > 0 && renderPagination(
                externalCurrentPage,
                externalTotalPages,
                paginateExternal,
                externalItemsPerPage,
                setExternalItemsPerPage,
                sortedExternalData.length,
                externalIndexOfFirstItem,
                externalIndexOfLastItem
              )}
            </div>
          </TabPane>
        </Tabs>
      </div>

      {/* Modal de detalles mejorado */}
      <Modal
        title="Detalles del Envío"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        className="pedido-detail-modal"
        width={800}
        footer={[
          <Button key="submit" type="primary" onClick={handleOk}>
            Cerrar
          </Button>
        ]}
      >
        {selectedOrder && (
          <div className="detail-info-grid">
            <div className="detail-section">
              <h3><BoxPlotOutlined /> Información del Producto</h3>
              <div className="detail-info-item">
                <label>Nombre del producto</label>
                <span>{selectedOrder?.nombre_producto || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Valor de venta</label>
                <span>${selectedOrder?.valor_venta?.toLocaleString() || '0'}</span>
              </div>
              <div className="detail-info-item">
                <label>Valor envío</label>
                <span>${selectedOrder?.valor_envio || '0'}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3><CarOutlined /> Información del Envío</h3>
              <div className="detail-info-item">
                <label>Transportadora</label>
                <span>{selectedOrder?.transportadora || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Estado envío</label>
                <span className={getStatusBadgeClass(selectedOrder?.estado_envio)}>
                  {selectedOrder?.estado_envio || 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3><UserOutlined /> Información del Destinatario</h3>
              <div className="detail-info-item">
                <label>Nombre completo</label>
                <span>{`${selectedOrder?.preeenvio?.destinatario?.nombre || ''} ${selectedOrder?.preeenvio?.destinatario?.primer_apellido || ''} ${selectedOrder?.preeenvio?.destinatario?.segundo_apellido || ''}`.trim() || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Teléfono</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.telefono || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Correo</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.correo || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Dirección</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.direccion || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Ciudad</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.ciudad || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProductTablePedidos;
